'use client';

/**
 * TierDisplay Component
 *
 * Visualizes tiered pricing structures with bar chart representation.
 * Supports volume and graduated tier types with current value indicator.
 * Uses glassmorphism styling with optional neon accent colors.
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import type React from 'react';
import { useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import type { PricingTier, TierType } from '../../types/pricingEngine';

// ============================================================================
// Types
// ============================================================================

export interface TierDisplayProps extends Omit<BoxProps, 'children'> {
  /** Array of pricing tiers to display */
  tiers: PricingTier[];
  /** Current value to highlight which tier applies */
  currentValue?: number;
  /** Type of tiered pricing: GRADUATED or MARGINAL */
  tierType: TierType;
  /** Custom formatter for values */
  formatValue?: (value: number) => string;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Display variant */
  variant?: 'compact' | 'detailed' | 'chart';
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  if (neonStyles) {
    return {
      borderRadius: 12,
      padding: theme.spacing(2),
      background: neonStyles.background,
      border: `1px solid ${neonStyles.border}`,
      transition: transitionPresets.card,
    };
  }

  return {
    borderRadius: 12,
    padding: theme.spacing(2),
    background: glass.background,
    backdropFilter: glass.backdropBlur,
    border: `1px solid ${glass.border}`,
    boxShadow: glass.shadow,
    transition: transitionPresets.card,
  };
});

const TierRow = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $isActive: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $isActive, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    transition: transitionPresets.allFast,
    background: $isActive
      ? neonStyles
        ? alpha(neonStyles.base, 0.15)
        : $isDark
          ? 'rgba(99, 91, 255, 0.15)'
          : 'rgba(99, 91, 255, 0.1)'
      : 'transparent',
    border: $isActive
      ? `1px solid ${neonStyles?.border || ($isDark ? 'rgba(99, 91, 255, 0.3)' : 'rgba(99, 91, 255, 0.2)')}`
      : '1px solid transparent',

    '&:last-child': {
      marginBottom: 0,
    },

    '&:hover': {
      background: $isActive
        ? neonStyles
          ? alpha(neonStyles.base, 0.2)
          : $isDark
            ? 'rgba(99, 91, 255, 0.2)'
            : 'rgba(99, 91, 255, 0.15)'
        : $isDark
          ? 'rgba(255, 255, 255, 0.03)'
          : 'rgba(0, 0, 0, 0.02)',
    },
  };
});

const TierBar = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $width: number;
  $isDark: boolean;
  $isActive: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $width, $isDark, $isActive, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const baseColor = neonStyles?.base || theme.palette.primary.main;

  return {
    height: 8,
    borderRadius: 4,
    width: `${$width}%`,
    minWidth: 8,
    background: $isActive
      ? `linear-gradient(90deg, ${baseColor} 0%, ${alpha(baseColor, 0.7)} 100%)`
      : $isDark
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)',
    boxShadow: $isActive ? `0 0 8px ${alpha(baseColor, 0.4)}` : 'none',
    transition: transitionPresets.allFast,
  };
});

const CompactTierItem = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $isActive: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $isActive, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    marginBottom: theme.spacing(0.5),
    background: $isActive
      ? neonStyles
        ? alpha(neonStyles.base, 0.12)
        : alpha(theme.palette.primary.main, 0.1)
      : 'transparent',
    borderLeft: $isActive ? `3px solid ${neonStyles?.base || theme.palette.primary.main}` : '3px solid transparent',
    transition: transitionPresets.allFast,

    '&:last-child': {
      marginBottom: 0,
    },
  };
});

const ChartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: theme.spacing(0.5),
  height: 120,
  padding: theme.spacing(2, 0),
}));

const ChartBar = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $height: number;
  $isDark: boolean;
  $isActive: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $height, $isDark, $isActive, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const baseColor = neonStyles?.base || theme.palette.primary.main;

  return {
    flex: 1,
    height: `${$height}%`,
    minHeight: 8,
    borderRadius: '4px 4px 0 0',
    background: $isActive
      ? `linear-gradient(180deg, ${baseColor} 0%, ${alpha(baseColor, 0.6)} 100%)`
      : $isDark
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.08)',
    boxShadow: $isActive ? `0 0 12px ${alpha(baseColor, 0.5)}` : 'none',
    transition: transitionPresets.all,
    cursor: 'pointer',

    '&:hover': {
      background: $isActive
        ? `linear-gradient(180deg, ${baseColor} 0%, ${alpha(baseColor, 0.75)} 100%)`
        : $isDark
          ? 'rgba(255, 255, 255, 0.18)'
          : 'rgba(0, 0, 0, 0.12)',
    },
  };
});

const CurrentValueIndicator = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;
  const baseColor = neonStyles?.base || theme.palette.primary.main;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 600,
    background: alpha(baseColor, 0.15),
    color: baseColor,
    border: `1px solid ${alpha(baseColor, 0.3)}`,
  };
});

// ============================================================================
// Helper Functions
// ============================================================================

const defaultFormatValue = (value: number): string => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toLocaleString();
};

const formatRate = (tier: PricingTier): string => {
  if (tier.rateType === 'percentage') {
    return `${tier.rate}%`;
  }
  return `$${tier.rate.toFixed(2)}`;
};

const getTierRangeLabel = (tier: PricingTier, formatValue: (value: number) => string): string => {
  if (tier.to === null) {
    return `${formatValue(tier.from)}+`;
  }
  return `${formatValue(tier.from)} - ${formatValue(tier.to)}`;
};

// ============================================================================
// Component
// ============================================================================

export const TierDisplay: React.FC<TierDisplayProps> = ({
  tiers,
  currentValue,
  tierType,
  formatValue = defaultFormatValue,
  neonAccent,
  variant = 'detailed',
  className,
  ...boxProps
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();

  // Find the active tier based on current value
  const activeTierIndex = useMemo(() => {
    if (currentValue === undefined) return -1;
    return tiers.findIndex((tier) => {
      if (tier.to === null) {
        return currentValue >= tier.from;
      }
      return currentValue >= tier.from && currentValue < tier.to;
    });
  }, [tiers, currentValue]);

  // Calculate bar widths based on tier rates
  const maxRate = useMemo(() => {
    return Math.max(...tiers.map((t) => t.rate));
  }, [tiers]);

  // Sort tiers by from value
  const sortedTiers = useMemo(() => {
    return [...tiers].sort((a, b) => a.from - b.from);
  }, [tiers]);

  if (sortedTiers.length === 0) {
    return (
      <Box className={className} {...boxProps}>
        <StyledContainer $isDark={isDark} $neonAccent={neonAccent}>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
            {t('tier.noTiersConfigured')}
          </Typography>
        </StyledContainer>
      </Box>
    );
  }

  // Compact variant - simple list
  if (variant === 'compact') {
    return (
      <Box className={className} {...boxProps}>
        <StyledContainer
          $isDark={isDark}
          $neonAccent={neonAccent}
          initial={motionPresets.fadeInUp.initial}
          animate={motionPresets.fadeInUp.animate}
          transition={motionPresets.fadeInUp.transition}
        >
          {currentValue !== undefined && (
            <Box sx={{ mb: 2 }}>
              <CurrentValueIndicator $isDark={isDark} $neonAccent={neonAccent}>
                {t('tier.currentValue', { value: formatValue(currentValue) })}
              </CurrentValueIndicator>
            </Box>
          )}

          {sortedTiers.map((tier, index) => (
            <CompactTierItem
              key={tier.id}
              $isDark={isDark}
              $isActive={index === activeTierIndex}
              $neonAccent={neonAccent}
            >
              <Typography
                variant="body2"
                sx={{
                  color: index === activeTierIndex ? 'text.primary' : 'text.secondary',
                  fontWeight: index === activeTierIndex ? 600 : 400,
                }}
              >
                {getTierRangeLabel(tier, formatValue)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color:
                    index === activeTierIndex
                      ? neonAccent
                        ? getNeonStyles(neonAccent, isDark).base
                        : 'primary.main'
                      : 'text.primary',
                }}
              >
                {formatRate(tier)}
                {tier.flatFee ? ` + $${tier.flatFee.toFixed(2)}` : ''}
              </Typography>
            </CompactTierItem>
          ))}

          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
            {tierType === 'GRADUATED' ? t('tier.graduated') : t('tier.marginal')}
          </Typography>
        </StyledContainer>
      </Box>
    );
  }

  // Chart variant - visual bar chart
  if (variant === 'chart') {
    return (
      <Box className={className} {...boxProps}>
        <StyledContainer
          $isDark={isDark}
          $neonAccent={neonAccent}
          initial={motionPresets.fadeInUp.initial}
          animate={motionPresets.fadeInUp.animate}
          transition={motionPresets.fadeInUp.transition}
        >
          {currentValue !== undefined && (
            <Box sx={{ mb: 2 }}>
              <CurrentValueIndicator $isDark={isDark} $neonAccent={neonAccent}>
                {t('tier.currentValue', { value: formatValue(currentValue) })}
              </CurrentValueIndicator>
            </Box>
          )}

          <ChartContainer>
            {sortedTiers.map((tier, index) => (
              <ChartBar
                key={tier.id}
                $height={(tier.rate / maxRate) * 100}
                $isDark={isDark}
                $isActive={index === activeTierIndex}
                $neonAccent={neonAccent}
                initial={{ height: 0 }}
                animate={{ height: `${(tier.rate / maxRate) * 100}%` }}
                transition={{
                  delay: index * 0.1,
                  duration: 0.3,
                  ease: 'easeOut',
                }}
                title={`${getTierRangeLabel(tier, formatValue)}: ${formatRate(tier)}`}
              />
            ))}
          </ChartContainer>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 1,
              px: 0.5,
            }}
          >
            {sortedTiers.map((tier, index) => (
              <Typography
                key={tier.id}
                variant="caption"
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  color: index === activeTierIndex ? 'text.primary' : 'text.secondary',
                  fontWeight: index === activeTierIndex ? 600 : 400,
                }}
              >
                {formatRate(tier)}
              </Typography>
            ))}
          </Box>

          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mt: 2,
              display: 'block',
              textAlign: 'center',
            }}
          >
            {tierType === 'GRADUATED' ? t('tier.graduatedPricing') : t('tier.marginalPricing')}
          </Typography>
        </StyledContainer>
      </Box>
    );
  }

  // Detailed variant (default) - full information with bars
  return (
    <Box className={className} {...boxProps}>
      <StyledContainer
        $isDark={isDark}
        $neonAccent={neonAccent}
        initial={motionPresets.fadeInUp.initial}
        animate={motionPresets.fadeInUp.animate}
        transition={motionPresets.fadeInUp.transition}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {tierType === 'GRADUATED' ? t('tier.graduatedTiers') : t('tier.marginalTiers')}
          </Typography>
          {currentValue !== undefined && (
            <CurrentValueIndicator $isDark={isDark} $neonAccent={neonAccent}>
              Current: {formatValue(currentValue)}
            </CurrentValueIndicator>
          )}
        </Box>

        {sortedTiers.map((tier, index) => (
          <TierRow
            key={tier.id}
            $isDark={isDark}
            $isActive={index === activeTierIndex}
            $neonAccent={neonAccent}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Box sx={{ minWidth: 100 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: index === activeTierIndex ? 600 : 400,
                  color: 'text.primary',
                }}
              >
                {getTierRangeLabel(tier, formatValue)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, px: 2 }}>
              <TierBar
                $width={(tier.rate / maxRate) * 100}
                $isDark={isDark}
                $isActive={index === activeTierIndex}
                $neonAccent={neonAccent}
                initial={{ width: 0 }}
                animate={{ width: `${(tier.rate / maxRate) * 100}%` }}
                transition={{ delay: index * 0.05 + 0.1, duration: 0.3 }}
              />
            </Box>

            <Box sx={{ minWidth: 80, textAlign: 'right' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color:
                    index === activeTierIndex
                      ? neonAccent
                        ? getNeonStyles(neonAccent, isDark).base
                        : 'primary.main'
                      : 'text.primary',
                }}
              >
                {formatRate(tier)}
              </Typography>
              {tier.flatFee !== undefined && tier.flatFee > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  + ${tier.flatFee.toFixed(2)} flat
                </Typography>
              )}
            </Box>
          </TierRow>
        ))}

        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2, display: 'block' }}>
          {tierType === 'GRADUATED' ? t('tier.graduatedDescription') : t('tier.marginalDescription')}
        </Typography>
      </StyledContainer>
    </Box>
  );
};

TierDisplay.displayName = 'TierDisplay';

export default TierDisplay;
